import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, Route, Navigate, Routes, useLocation, useNavigate } from "react-router-dom";
import api from "./api";
import { AuthContext } from "./context/AuthContext";
import { restoreSession } from "./pwa/authKeeper";
import Navbar from "./components/Navbar";
import Login from "./components/Login";
import Landing from "./components/Landing";
import ProductForm from "./components/ProductForm";
import ProductImport from "./components/ProductImport";
import StockEntry from "./components/StockEntry";
import StockExit from "./components/StockExit";
import InventoryList from "./components/InventoryList";
import Alerts from "./components/Alerts";
import BatchReport from "./components/BatchReport";
import StockMovementReport from "./components/StockMovementReport";
import CostVariationReport from "./components/CostVariationReport";
import StockValueReport from "./components/StockValueReport";
import PurchasePrediction from './components/PurchasePrediction';
import FichasTecnicasList from './components/FichasTecnicasList';
import Etiquetas from './components/Etiquetas';
import EtiquetaResolver from './components/EtiquetaResolver';
import FichaTecnicaForm from './components/FichaTecnicaForm';
import FichaTecnicaDetalhes from './components/FichaTecnicaDetalhes';
import Embalagens from './components/Embalagens';
import Dashboard from './components/Dashboard';
import Inventario from './components/Inventario';
import Clientes from './components/Clientes';
import Pedidos from './components/Pedidos';
import Orcamentos from './components/Orcamentos';
import GuiaTamanhos from './components/GuiaTamanhos';
import GuiaRecheios from './components/GuiaRecheios';
import GuiaCombinacoes from './components/GuiaCombinacoes';
import GuiaProducaoSemanal from './components/GuiaProducaoSemanal';
import GuiaMontagemDiaria from './components/GuiaMontagemDiaria';
import CalculadoraProducao from './components/CalculadoraProducao';
import Financas from './components/Financas';
import Atalhos from './components/Atalhos';
import TenantRegister from './components/TenantRegister';
import TenantSettings from './components/TenantSettings';
import EsqueciSenha from './components/EsqueciSenha';
import ResetarSenha from './components/ResetarSenha';
import Footer from './components/Footer';
import PaginasLegais from './components/PaginasLegais';
import ExcluirContaPublica from './components/ExcluirContaPublica';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import PrimeirosPassos from './components/PrimeirosPassos';
import { MAI_ATIVA } from './config/features';
import { moduloLigado, MODULO_ETIQUETAS } from './config/modulos';
// Lazy: com a Mai desligada esses chunks nunca sao baixados.
const MontagemGuiada = React.lazy(() => import('./components/MontagemGuiada'));
const MaiModal = React.lazy(() => import('./components/MaiModal'));
import ConsentModal from './components/ConsentModal';
import Assinatura from './components/Assinatura';

import { instalar as instalarDeepLinks } from "./platform/deeplinks";
import "./App.css";
import { ToastContainer } from 'react-toastify';
import Novidades from './components/Novidades';

// Tutoriais (rota pública) — carregada sob demanda
const AjudaHome = React.lazy(() => import("./ajuda/AjudaHome"));
const AjudaArtigo = React.lazy(() => import("./ajuda/AjudaArtigo"));
const AjudaFallback = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8C7A7A' }}>
    Carregando…
  </div>
);

/**
 * O FAB flutua sobre o conteudo e ja escondeu controles duas vezes (o toggle em
 * Configuracoes, o "Concluido" da montagem). Este hook detecta se ha um
 * elemento interativo logo abaixo dele e, nesse caso, faz o FAB CEDER: fica
 * translucido e deixa de capturar toque, entao o dedo alcanca o controle.
 *
 * Usa elementsFromPoint (que devolve a pilha inteira no ponto) em vez de
 * esconder o FAB para medir — esconder causaria piscada.
 */
const SELETOR_CONTROLE = 'button, a, input, select, textarea, [role="switch"], [role="button"], label';

function useCederParaControle(ref) {
  const [cedendo, setCedendo] = useState(false);

  useEffect(() => {
    let raf = null;
    const medir = () => {
      raf = null;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const pilha = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(x, y)
        : [];
      // Pula o proprio FAB e seus filhos; olha o que esta atras.
      const atras = pilha.filter(n => n !== el && !el.contains(n));
      setCedendo(atras.some(n => n.closest && n.closest(SELETOR_CONTROLE)));
    };
    const agendar = () => { if (raf === null) raf = requestAnimationFrame(medir); };

    medir();
    window.addEventListener('scroll', agendar, true);   // true: pega scroll de containers internos
    window.addEventListener('resize', agendar);
    const t = setInterval(agendar, 700);   // conteudo que muda sem scroll (modais, listas)
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', agendar, true);
      window.removeEventListener('resize', agendar);
      clearInterval(t);
    };
  }, [ref]);

  return cedendo;
}

// Preferencia "mostrar o botao flutuante da Mai" (Configuracoes).
export function maiFabHabilitado() {
  try {
    return localStorage.getItem('mai_fab_disabled') !== 'true';
  } catch {
    return true;
  }
}

function PrimeirosPassosFab() {
  // Inicializa com cache para evitar piscada, mas SEMPRE revalida no mount.
  const [completo, setCompleto] = useState(localStorage.getItem('onboarding_completo') === 'true');
  const [dismissed, setDismissed] = useState(sessionStorage.getItem('primeiros_passos_fab_dismissed') === 'true');

  useEffect(() => {
    let alive = true;
    api.get('/onboarding/status').then(res => {
      if (!alive) return;
      const novo = !!res.data.completo;
      setCompleto(novo);
      localStorage.setItem('onboarding_completo', String(novo));
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (completo || dismissed) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 90,
      display: 'flex', alignItems: 'center', gap: 10,
      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 999,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      padding: '8px 14px 8px 8px',
    }}>
      <Link to="/primeiros-passos" style={{
        display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#1F2937',
      }}>
        <span style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'linear-gradient(135deg, #C4786E, #E8A85C)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
        }}>🚀</span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Continuar primeiros passos</span>
      </Link>
      <button
        onClick={(e) => { e.preventDefault(); sessionStorage.setItem('primeiros_passos_fab_dismissed', 'true'); setDismissed(true); }}
        title="Ocultar"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#9CA3AF', fontSize: 16, padding: 4, lineHeight: 1,
        }}
      >×</button>
    </div>
  );
}

function MaiFab({ onAbrir }) {
  // Inicializa com cache para evitar piscada, mas SEMPRE revalida no mount.
  const [completo, setCompleto] = useState(localStorage.getItem('onboarding_completo') === 'true');
  const [dismissed, setDismissed] = useState(sessionStorage.getItem('primeiros_passos_fab_dismissed') === 'true');
  const [habilitado, setHabilitado] = useState(maiFabHabilitado);
  const fabRef = useRef(null);
  const cedendo = useCederParaControle(fabRef);

  // Reage ao toggle em Configuracoes sem precisar recarregar a tela.
  useEffect(() => {
    const h = () => setHabilitado(maiFabHabilitado());
    window.addEventListener('mai-fab-pref-changed', h);
    return () => window.removeEventListener('mai-fab-pref-changed', h);
  }, []);

  useEffect(() => {
    let alive = true;
    api.get('/onboarding/status').then(res => {
      if (!alive) return;
      const novo = !!res.data.completo;
      setCompleto(novo);
      localStorage.setItem('onboarding_completo', String(novo));
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Onboarding concluido: botao permanente da Mai (copiloto). Ainda em andamento:
  // pilula que leva pra tela cheia guiada, com opcao de ocultar. Em ambos os
  // casos o FAB existe no canto — o body reserva espaco (has-onboarding-fab).
  const visivel = habilitado && (completo || !dismissed);
  // Marca o body enquanto o FAB existe: o CSS usa isso para reservar espaco no
  // fim do .main-content, senao o FAB pousa em cima da ultima linha de cards.
  useEffect(() => {
    document.body.classList.toggle('has-onboarding-fab', visivel);
    return () => document.body.classList.remove('has-onboarding-fab');
  }, [visivel]);

  if (!visivel) return null;

  // Copiloto permanente: abre o chat da Mai em modal sobre qualquer tela.
  if (completo) {
    return (
      <button
        ref={fabRef}
        onClick={onAbrir}
        title="Falar com a Mai"
        className="fab-onboarding"
        style={{
          // Soma a barra de gestos/navegacao: sem isso o FAB fica cortado embaixo.
          position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom))', right: 24, zIndex: 90,
          cursor: 'pointer',
          // Cede a vez quando ha um controle embaixo: some da frente e deixa o
          // toque passar, em vez de roubar o clique de um botao.
          opacity: cedendo ? 0.25 : 1,
          pointerEvents: cedendo ? 'none' : 'auto',
          transition: 'opacity .2s',
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          padding: '8px 16px 8px 8px',
        }}>
        <img src="/mai-avatar.png" alt="Mai" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
        <span className="fab-onboarding-label" style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>Falar com a Mai</span>
      </button>
    );
  }

  // Onboarding em andamento: pilula pra retomar os primeiros passos guiados.
  return (
    <div ref={fabRef} className="fab-onboarding" style={{
      // Soma a barra de gestos/navegacao: sem isso o FAB fica cortado embaixo.
      position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom))', right: 24, zIndex: 90,
      opacity: cedendo ? 0.25 : 1,
      pointerEvents: cedendo ? 'none' : 'auto',
      transition: 'opacity .2s',
      display: 'flex', alignItems: 'center', gap: 10,
      background: '#fff', border: '1px solid #E5E7EB', borderRadius: 999,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      padding: '8px 14px 8px 8px',
    }}>
      <Link to="/primeiros-passos" style={{
        display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#1F2937',
      }}>
        <img src="/mai-avatar.png" alt="Mai" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
        <span className="fab-onboarding-label" style={{ fontSize: 13, fontWeight: 600 }}>Continuar primeiros passos</span>
      </Link>
      <button
        onClick={(e) => { e.preventDefault(); sessionStorage.setItem('primeiros_passos_fab_dismissed', 'true'); setDismissed(true); }}
        title="Ocultar"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#9CA3AF', fontSize: 16, padding: 4, lineHeight: 1,
        }}
      >×</button>
    </div>
  );
}

// Fora do App() de proposito: declarada dentro, ela virava uma funcao nova a
// cada render do App, e o React trata funcao nova como componente de tipo
// diferente — desmontando e remontando toda a subarvore de rotas.
function HomeRoute() {
  // Cache só serve para evitar piscar; SEMPRE revalida no backend.
  const cachedCompleto = localStorage.getItem('onboarding_completo') === 'true';
  const [status, setStatus] = useState(cachedCompleto ? true : null);

  useEffect(() => {
    let alive = true;
    api.get('/onboarding/status').then(res => {
      if (!alive) return;
      const novo = !!res.data.completo;
      setStatus(novo);
      localStorage.setItem('onboarding_completo', String(novo));
    }).catch(() => {
      if (!alive) return;
      // Em erro de rede, mantém o status do cache (não força redirect).
      setStatus(prev => prev === null ? true : prev);
    });
    return () => { alive = false; };
  }, []);

  if (status === null) return null;
  return status ? <Dashboard /> : <Navigate to="/primeiros-passos" replace />;
}

function App() {
  const { isAuthenticated, login, logout, tenant } = useContext(AuthContext);
  const temEtiquetas = moduloLigado(tenant, MODULO_ETIQUETAS);
  const [editProduct, setEditProduct] = useState(null);
  const [maiAberta, setMaiAberta] = useState(false);
  const location = useLocation();

  // Landing pública em / quando não autenticado — trata como página pública (sem sidebar, sem padding do main-content)
  const isPublicHome = location.pathname === "/" && !isAuthenticated;
  const isLoginPage = ["/login", "/register", "/esqueci-senha", "/resetar-senha", "/privacidade", "/termos", "/excluir-conta"].includes(location.pathname)
    || location.pathname === "/ajuda" || location.pathname.startsWith("/ajuda/")
    || isPublicHome;
  const isAssinaturaPage = location.pathname === "/assinar";
  // Toque na notificacao push: o push.js dispara o evento porque quem tem o
  // router e o App. Navegar por window.location recarregaria o bundle inteiro.
  const navigate = useNavigate();
  useEffect(() => {
    const ir = (e) => { if (e.detail) navigate(e.detail); };
    window.addEventListener('push-navegar', ir);
    return () => window.removeEventListener('push-navegar', ir);
  }, [navigate]);

  // Link de metria.demiplie.com.br aberto fora do app (ex.: e-mail de
  // recuperacao de senha) cai aqui e vira navegacao interna.
  useEffect(() => instalarDeepLinks((caminho) => navigate(caminho)), [navigate]);

  const isChatMai = location.pathname === "/primeiros-passos" || location.pathname === "/assistente";

  // Buscamos billing/status pra saber se past_due esta em grace (acesso liberado)
  // ou se ja expirou (bloqueia). Sem isso bloqueariamos no primeiro fail de cobranca.
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    if (!isAuthenticated || isLoginPage) { setBilling(null); return; }
    let alive = true;
    api.get('/billing/status').then(res => {
      if (alive) setBilling(res.data);
    }).catch(() => { /* mantem null — fallback no tenant.subscription_status */ });
    return () => { alive = false; };
  }, [isAuthenticated, isLoginPage, location.pathname]);

  const billingStatus = billing?.subscription_status || tenant?.subscription_status;
  const inGrace = !!billing?.in_grace;

  const needsSubscription = isAuthenticated && tenant &&
    ['pending_payment', 'canceled'].includes(billingStatus || '') ||
    (billingStatus === 'past_due' && !inGrace);

  const [consentOk, setConsentOk] = useState(null);
  useEffect(() => {
    if (!isAuthenticated || isLoginPage) { setConsentOk(true); return; }
    api.get('/consent/status').then(res => {
      setConsentOk(res.data.termos_aceito && res.data.privacidade_aceito);
    }).catch(() => setConsentOk(true));
  }, [isAuthenticated, isLoginPage]);

  // Re-roda o keeper a cada navegacao. Em PWA iOS o app pode ficar horas
  // suspenso entre cliques — sem isso, a primeira request da rota nova
  // virava 401 e disparava o fallback do interceptor (que tambem funciona,
  // mas piora UX). Idempotente: noop se token ainda esta fresco.
  useEffect(() => {
    if (!isAuthenticated || isLoginPage) return;
    restoreSession({ reason: `route:${location.pathname}` }).catch(() => {});
  }, [location.pathname, isAuthenticated, isLoginPage]);

  const handleEditProduct = (produto) => {
    setEditProduct(produto);
  };

  // Funcao comum, NAO componente: chamada em vez de renderizada, ela nao cria
  // uma fronteira de componente — entao o React reconcilia o elemento no lugar
  // em vez de remontar a tela a cada re-render do App.
  const privateRoute = (element, allowInactive = false) => {
    if (!isAuthenticated) {
      // Guarda para onde voltar. O interceptor de 401 do api.js ja fazia isso
      // quando o token expirava DURANTE o uso, mas quem abre o app por um link
      // — o QR de uma etiqueta, por exemplo — chega aqui sem nenhuma chamada
      // ter acontecido, e caia no login perdendo o destino. O celular que ficou
      // dias parado no bolso e justamente o caso mais comum de ler um QR.
      try {
        const destino = location.pathname + location.search;
        if (destino && destino !== '/' && !destino.startsWith('/login')) {
          sessionStorage.setItem('post_login_redirect', destino);
        }
      } catch { /* sessionStorage cheio ou desativado: segue sem */ }
      return <Navigate to="/login" />;
    }
    if (needsSubscription && !allowInactive) return <Navigate to="/assinar" replace />;
    return element;
  };




  // O `tenant` vem do login e nao se atualiza sozinho: quem assinou — ou quem
  // teve o acesso liberado no suporte — continuava vendo "período de teste" ate
  // sair e entrar de novo, mesmo com a cobranca ja resolvida. O /billing/status
  // e a fonte fresca (o outro banner desta tela ja usava so ele); o tenant fica
  // como reserva para o instante antes de a chamada voltar.
  const isTrial = isAuthenticated && (
    billing ? billing.subscription_status === 'trial'
            : (tenant?.subscription_status === 'trial' && !!tenant?.trial_ends_at)
  );
  let trialDaysLeft = null;
  if (isTrial) {
    if (billing && billing.trial_days_left != null) {
      trialDaysLeft = billing.trial_days_left;
    } else if (tenant?.trial_ends_at) {
      const ms = new Date(tenant.trial_ends_at).getTime() - Date.now();
      // Round evita off-by-one por causa de fração de segundos entre criação e leitura.
      trialDaysLeft = Math.max(0, Math.round(ms / 86400000));
    }
  }

  // O React Router preserva a posicao de rolagem entre rotas, entao a tela
  // seguinte abria no meio — as vezes no fim da lista, com o titulo escondido
  // atras da topbar fixa. Na web quase nao se nota; no celular parecia area
  // segura quebrada, e era so o scroll.
  //
  // `document.scrollingElement` e nao `window.scrollTo`: no WKWebView com
  // contentInset quem rola e o elemento, e o window ignora o comando.
  useEffect(() => {
    const alvo = document.scrollingElement || document.documentElement;
    alvo.scrollTop = 0;
    const conteudo = document.querySelector('.main-content');
    if (conteudo) conteudo.scrollTop = 0;
  }, [location.pathname]);

  // No celular a faixa e `fixed` (a topbar tem z-index maior e a cobriria).
  // Fixed sai do fluxo, entao o conteudo precisa ganhar o espaco dela — e essa
  // classe no body que faz isso, em vez de a faixa empurrar sozinha.
  const temFaixa = !isLoginPage && (
    (isTrial && trialDaysLeft !== null) ||
    (isAuthenticated && billingStatus === 'past_due' && inGrace)
  );
  useEffect(() => {
    document.body.classList.toggle('tem-faixa', !!temFaixa);
    return () => document.body.classList.remove('tem-faixa');
  }, [temFaixa]);

  return (
    <>
      {isAuthenticated && !isLoginPage && consentOk === false && (
        <ConsentModal onComplete={() => setConsentOk(true)} />
      )}
      {isAuthenticated && !isLoginPage && billingStatus === 'past_due' && inGrace && (
        <div className="faixa-topo" style={{
          background: '#E8A85C', color: '#fff',
          padding: '8px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <span>
            ⚠️ Pagamento da assinatura falhou. Atualize seu cartão em
            até <strong>{billing?.grace_days_left ?? 0} dia{billing?.grace_days_left === 1 ? '' : 's'}</strong> para
            manter o acesso.
          </span>
          <Link to="/assinar" style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700, padding: '6px 4px' }}>
            Atualizar cartão
          </Link>
        </div>
      )}
      {isTrial && !isLoginPage && trialDaysLeft !== null && (
        <div className="faixa-topo" style={{
          background: trialDaysLeft <= 3 ? '#E8A85C' : '#C4786E', color: '#fff',
          padding: '8px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <span>🎁 Período de teste: {trialDaysLeft} {trialDaysLeft === 1 ? 'dia' : 'dias'} restante{trialDaysLeft === 1 ? '' : 's'}</span>
          <Link to="/assinar" style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700, padding: '6px 4px' }}>Assinar agora</Link>
        </div>
      )}
      {!isLoginPage && <Navbar isAuthenticated={isAuthenticated} onLogout={logout} />}
      {isAuthenticated && !isLoginPage && (
        MAI_ATIVA
          ? (!isChatMai && location.pathname !== '/configuracoes' && <MaiFab onAbrir={() => setMaiAberta(true)} />)
          : (location.pathname !== '/primeiros-passos' && <PrimeirosPassosFab />)
      )}
      {MAI_ATIVA && isAuthenticated && !isLoginPage && (
        <React.Suspense fallback={null}>
          <MaiModal open={maiAberta} onClose={() => setMaiAberta(false)} />
        </React.Suspense>
      )}
      {/* Um container so, no topo da arvore. Antes ele morava DENTRO do
          InventoryList, entao todo `toast()` disparado de outra tela era
          engolido em silencio — inclusive os avisos do leitor de codigo, que
          fica na barra de cima e aparece em todas as telas. */}
      {/* Depois do consentimento, nunca junto: dois modais empilhados no
          primeiro login e o caminho mais curto para a pessoa fechar os dois sem
          ler nenhum. O consentimento e obrigacao legal e vem primeiro. */}
      {isAuthenticated && !isLoginPage && consentOk === true && <Novidades />}

      <ToastContainer position="top-right" autoClose={5000} newestOnTop={false}
        closeOnClick pauseOnHover />

      <div className={isLoginPage ? "" : "main-content"} style={isLoginPage ? {} : { padding: '24px 16px' }}>
        <Routes>
          <Route path="/login" element={<Login onLoginSuccess={login} />} />
          <Route path="/register" element={<TenantRegister />} />
          <Route path="/esqueci-senha" element={<EsqueciSenha />} />
          {/* Publicas: as lojas exigem politica e caminho de exclusao
              acessiveis sem login — inclusive para o revisor, que nao tem conta. */}
          <Route path="/privacidade" element={<PaginasLegais />} />
          <Route path="/termos" element={<PaginasLegais />} />
          <Route path="/excluir-conta" element={<ExcluirContaPublica />} />
          <Route path="/resetar-senha" element={<ResetarSenha />} />

          <Route path="/ajuda" element={<React.Suspense fallback={<AjudaFallback />}><AjudaHome /></React.Suspense>} />
          <Route path="/ajuda/:slug" element={<React.Suspense fallback={<AjudaFallback />}><AjudaArtigo /></React.Suspense>} />

          <Route path="/assinar" element={privateRoute(<Assinatura />, true)} />
          <Route path="/" element={isAuthenticated ? privateRoute(<HomeRoute />) : <Landing />} />
          <Route path="/primeiros-passos" element={privateRoute(
            MAI_ATIVA
              ? <React.Suspense fallback={null}><MontagemGuiada /></React.Suspense>
              : <PrimeirosPassos />
          )} />
          {MAI_ATIVA && (
            <Route path="/assistente" element={privateRoute(
              <React.Suspense fallback={null}><MontagemGuiada /></React.Suspense>
            )} />
          )}
          <Route path="/produtos" element={privateRoute(<ProductForm produtoEditado={editProduct} />)} />
          <Route path="/produtos-sem-controle" element={<Navigate to="/estoque" replace />} />
          <Route path="/embalagens" element={privateRoute(<Embalagens />)} />

          <Route path="/entrada" element={privateRoute(<StockEntry />)} />
          <Route path="/saida" element={privateRoute(<StockExit />)} />

          <Route path="/estoque" element={privateRoute(<InventoryList onEditProduct={handleEditProduct} />)} />
          <Route path="/alertas" element={privateRoute(<Alerts />)} />
          {/* O Controle de Lotes virou uma aba da tela de Validade. O caminho
              antigo continua vivo porque ha quem tenha favorito e link salvo —
              e porque quebrar URL para reorganizar menu e caro e invisivel. */}
          <Route path="/lotes" element={
            temEtiquetas
              ? <Navigate to="/etiquetas?aba=estoque" replace />
              : privateRoute(<BatchReport />)
          } />
          <Route path="/movimentacoes" element={privateRoute(<StockMovementReport />)} />
          <Route path="/variacao-custo" element={privateRoute(<CostVariationReport />)} />
          <Route path="/valor-estoque" element={privateRoute(<StockValueReport />)} />
          <Route path="/prediction" element={privateRoute(<PurchasePrediction />)} />

          <Route path="/fichas-tecnicas" element={privateRoute(<FichasTecnicasList />)}/>
          {temEtiquetas && (
            <Route path="/etiquetas" element={privateRoute(<Etiquetas />)} />
          )}
          {/* /e/:codigo e o endereco impresso no QR. Fica FORA da flag: papel
              ja impresso nao pode virar link morto se a feature for desligada. */}
          <Route path="/e/:codigo" element={privateRoute(<EtiquetaResolver />)} />
          <Route path="/fichas-tecnicas/base" element={privateRoute(<FichasTecnicasList tipoFixo="preparacao_base" />)}/>
          <Route path="/fichas-tecnicas/produto" element={privateRoute(<FichasTecnicasList tipoFixo="produto_final" />)}/>
          <Route path="/fichas-tecnicas/nova" element={privateRoute(<FichaTecnicaForm />)} />
          <Route path="/fichas-tecnicas/nova/base" element={privateRoute(<FichaTecnicaForm tipoFixo="preparacao_base" />)} />
          <Route path="/fichas-tecnicas/nova/produto" element={privateRoute(<FichaTecnicaForm tipoFixo="produto_final" />)} />
          <Route path="/fichas-tecnicas/:id/editar" element={privateRoute(<FichaTecnicaForm />)} />
          <Route path="/fichas-tecnicas/:id" element={privateRoute(<FichaTecnicaDetalhes />)} />

          <Route path="/guia/tamanhos" element={privateRoute(<GuiaTamanhos />)} />
          <Route path="/guia/recheios" element={privateRoute(<GuiaRecheios />)} />
          <Route path="/guia/combinacoes" element={privateRoute(<GuiaCombinacoes />)} />

          <Route path="/guia/producao" element={privateRoute(<GuiaProducaoSemanal />)} />
          <Route path="/guia/montagem" element={privateRoute(<GuiaMontagemDiaria />)} />
          <Route path="/guia/calculadora" element={privateRoute(<CalculadoraProducao />)} />
          <Route path="/financas" element={privateRoute(<Financas />)} />
          <Route path="/atalhos" element={privateRoute(<Atalhos />)} />

          <Route path="/importar" element={privateRoute(<ProductImport />)} />
          <Route path="/inventario" element={privateRoute(<Inventario />)} />
          <Route path="/clientes" element={privateRoute(<Clientes />)} />
          <Route path="/pedidos" element={privateRoute(<Pedidos />)} />
          <Route path="/orcamentos" element={privateRoute(<Orcamentos />)} />
          <Route path="/configuracoes" element={privateRoute(<TenantSettings />)} />
          <Route path="/super-admin" element={privateRoute(<SuperAdminDashboard />)} />
        </Routes>
      </div>
      {/* Telas de chat da Mai ocupam a altura toda: o rodape roubaria ~160px de
          uma tela pequena e ainda ficaria metade atras da barra de navegacao.
          As informacoes dele seguem em todas as outras paginas. */}
      {!isChatMai && <Footer compact={isLoginPage} />}
    </>
  );
}

export default App;
